import type ApolloClient from 'apollo-client';
import gql from 'graphql-tag';

const GET_CUSTOM_CONTENT = gql`
	query CustomContent($id: ID!, $type: String!) {
		content(id: $id) {
			nodes {
				ari
				children(first: 100, type: $type) {
					nodes {
						body {
							atlas_doc_format {
								value
							}
						}
						title
					}
				}
			}
		}
	}
`;

export type CustomContentResponse = {
	data: {
		content: {
			nodes: {
				ari: string;
				children: {
					nodes: {
						body: {
							atlas_doc_format: {
								value: string;
							};
						};
						title: string;
					}[];
				};
			}[];
		};
	};
};

const formatResponseNodes = (
	content: CustomContentResponse['data']['content']['nodes'][0]['children']['nodes'],
): Record<string, any> | null => {
	return content.reduce((acc, node) => {
		return {
			...acc,
			[node.title]: JSON.parse(node.body.atlas_doc_format.value),
		};
	}, {});
};

// Content type -> container ID -> keyed children (value)
const containerChildrenContentCache: Record<
	string,
	Record<
		string,
		{
			promise: Promise<Record<string, any> | null> | null;
			values: Record<string, any> | null;
		}
	>
> = {};

export const getCachedTypedChildrenContent = ({
	contentContainerId,
	childContentType,
}: {
	childContentType: string;
	contentContainerId: string;
}) => {
	return containerChildrenContentCache[childContentType]?.[contentContainerId]?.values;
};

export const queryTypedChildrenContent = async ({
	apolloClient,
	contentContainerId,
	childContentType,
}: {
	apolloClient: ApolloClient<object>;
	childContentType: string;
	contentContainerId: string;
}) => {
	const containerCache = (containerChildrenContentCache[childContentType] ??= {});
	const contentCache = (containerCache[contentContainerId] ??= { promise: null, values: null });

	const children = (contentCache.promise ??= apolloClient
		.query({
			query: GET_CUSTOM_CONTENT,
			variables: {
				id: contentContainerId,
				type: childContentType,
			},
			// Manual cache management in place.
			fetchPolicy: 'network-only',
		})
		.then(({ data }) => data.content?.nodes?.[0]?.children?.nodes || [])
		.then((responseNodes) => {
			if (!responseNodes) {
				return null;
			}
			const content = formatResponseNodes(responseNodes);
			contentCache.values = content;
			return content;
		})
		.catch(() => null));

	return children;
};
